import { policyById, policyGuides, policyTopics, topicById, visiblePolicies } from "@content";
import { answerSheetSchema, type AnswerSheet, type PolicyGuide, type PolicyTopicId, type Profile } from "@contracts";
import { calculateImpact } from "./calculate";

const normalized = (value: string) => value.toLocaleLowerCase("ko-KR").replace(/[\s?!.,·/()[\]{}'\"“”‘’_-]+/g, "");

const corpusExamplesByTopic = new Map(
  policyGuides.map((guide) => [guide.topicId, new Set(guide.questionExamples.map(normalized))])
);

const score = (query: string, values: string[]) => {
  const text = normalized(query);
  return values.reduce((total, value) => total + (text.includes(normalized(value)) ? Math.max(2, normalized(value).length) : 0), 0);
};

const policyMatchRules = [
  {id: "POL-WORK-001", year: "2027", any: ["최저임금", "최저시급", "10700"], prefer: ["2027"]},
  {id: "POL-WORK-002", year: "2026", any: ["최저임금", "최저시급", "10320"], require: ["2026"]},
  {id: "POL-PENSION-001", year: "2026", any: ["국민연금", "연금보험료", "보험료율"], prefer: ["2026", "9.5"]},
  {id: "POL-PENSION-002", year: "2027", any: ["국민연금", "연금보험료", "보험료율"], require: ["2027"]},
  {id: "POL-PENSION-003", any: ["출산크레딧", "출산 크레딧"]},
  {id: "POL-PENSION-004", any: ["군복무크레딧", "군복무 크레딧"]},
  {id: "POL-HEALTH-001", year: "2026", any: ["건강보험료율", "건강보험료", "건강보험"], prefer: ["2026", "7.19"]},
  {id: "POL-CARE-001", any: ["장애아가족", "장애아 돌봄", "장애아동 돌봄", "장애아 양육"]}
] as const;

export const answerPolicyIds = policyMatchRules.map((rule) => rule.id);

function topicScores(query: string) {
  const text = normalized(query);
  return policyTopics
    .map((topic) => ({
      topic,
      score: score(query, [topic.label, topic.question, ...topic.keywords]) + (corpusExamplesByTopic.get(topic.id)?.has(text) ? 1_000 : 0)
    }))
    .sort((a, b) => b.score - a.score);
}

function policyMatches(query: string) {
  const text = normalized(query);
  const queryYears = [...query.matchAll(/(?:^|\D)(20\d{2})(?=\D|$)/g)].map((match) => match[1]);
  const supportedYears = new Set<string>(policyMatchRules.flatMap((rule) => "year" in rule ? [rule.year] : []));
  if (queryYears.some((year) => !supportedYears.has(year))) return [];
  const yearSensitive = /얼마|금액|요율|보험료|부담|내는\s*돈|월급|시급/.test(query);
  return policyMatchRules
    .flatMap((rule) => {
      const policy = policyById.get(rule.id);
      if (!policy || !visiblePolicies.some((item) => item.id === rule.id)) return [];
      const ruleYear = "year" in rule ? rule.year : null;
      if (queryYears.length > 0 && (!ruleYear || !queryYears.includes(ruleYear))) return [];
      if (queryYears.length === 0 && ruleYear && yearSensitive) return [];
      const require = "require" in rule ? rule.require : [];
      if (require.length > 0 && !require.every((value) => text.includes(normalized(value)))) return [];
      const hits = rule.any.filter((value) => text.includes(normalized(value)));
      if (hits.length === 0) return [];
      const preference = "prefer" in rule ? rule.prefer.filter((value) => text.includes(normalized(value))).length : 0;
      const requiredSpecificity = require.length * 40;
      return [{policy, score: hits.reduce((total, value) => total + normalized(value).length, 0) + preference * 20 + requiredSpecificity}];
    })
    .sort((a, b) => b.score - a.score);
}

function guideFor(topicId: PolicyTopicId): PolicyGuide {
  return policyGuides.find((guide) => guide.topicId === topicId) ?? policyGuides[0];
}

export function resolvePolicyQuestion(rawQuery: string, profile?: Profile, now = new Date()): AnswerSheet {
  const query = rawQuery.trim().slice(0, 500);
  if (query.length < 2) throw new Error("question_too_short");

  const topics = topicScores(query);
  const matchedTopics = topics.filter((row) => row.score > 0).slice(0, 2).map((row) => row.topic.id);
  const topicId = matchedTopics[0] ?? "civic_law";
  const guide = guideFor(topicId);
  const matches = policyMatches(query);
  const exact = matches[0]?.policy;
  const ambiguous = !exact && matchedTopics.length > 1 && topics[0].score === topics[1].score;

  if (!exact && matchedTopics.length === 0) {
    return answerSheetSchema.parse({
      query,
      outcome: "official_handoff",
      title: "어떤 정책인지 조금 더 알려주세요",
      summary: "정책 이름을 몰라도 괜찮습니다. 지역과 생활 사건을 한 가지 더 적거나 공식 정부 서비스에서 담당 기관을 확인할 수 있어요.",
      topicIds: ["civic_law"],
      matchedPolicyIds: [],
      confidence: "needs_context",
      facts: ["공식 원문과 현재 시행 상태를 찾은 뒤에만 개인 영향을 확정합니다."],
      unknowns: ["어느 지역·생활 영역의 정책인지", "발표·법안·시행 정책 중 무엇인지"],
      nextActions: [{label: "지역이나 생활 사건을 더 적기", url: null}, {label: "정부24에서 공식 서비스 찾기", url: "https://www.gov.kr/"}],
      citations: [{title: "정부24", publisher: "행정안전부", url: "https://www.gov.kr/", checkedAt: "2026-08-24"}],
      resolvedAt: now.toISOString()
    });
  }

  if (ambiguous) {
    return answerSheetSchema.parse({
      query,
      outcome: "clarify",
      title: "어느 생활 변화인지 한 가지만 더 알려주세요",
      summary: `${matchedTopics.map((id) => topicById.get(id)?.label).filter(Boolean).join(" 또는 ")} 가운데 무엇이 가장 궁금한가요? 지역·직업·가구 변화 중 하나를 더하면 범위를 좁힐 수 있습니다.`,
      topicIds: matchedTopics,
      matchedPolicyIds: [],
      confidence: "needs_context",
      facts: ["질문 원문은 서버 분석 로그에 저장하지 않습니다."],
      unknowns: ["가장 먼저 확인할 생활 영역"],
      nextActions: [{label: "조건 한 가지를 더해 다시 묻기", url: null}],
      citations: [{title: guide.title, publisher: guide.publisher, url: guide.actionUrl, checkedAt: guide.reviewedAt}],
      resolvedAt: now.toISOString()
    });
  }

  if (exact) {
    const impact = profile ? calculateImpact(exact, profile) : null;
    const facts = [exact.summary, ...exact.issueBalance.established.slice(0, 2), ...(impact ? [`내 조건 대조: ${impact.headline}`] : [])];
    const unknowns = [...new Set([...exact.issueBalance.considerations, ...(impact?.needsConfirmation ?? [])])];
    return answerSheetSchema.parse({
      query,
      outcome: "answer",
      title: exact.shortTitle,
      summary: exact.stage === "effective" ? "현재 시행 중인 정책입니다. 내 조건을 대조해 영향표를 계산할 수 있어요." : exact.stage === "notified" ? `결정·고시됐고 ${exact.effectiveAt ?? "표시된 적용일"}부터 적용됩니다.` : exact.stage === "promulgated" ? `공포됐고 ${exact.effectiveAt ?? "표시된 시행일"}부터 적용됩니다.` : "아직 진행 중인 정책입니다. 확정된 제도처럼 계산하지 않습니다.",
      topicIds: matchedTopics.length > 0 ? matchedTopics : [topicId],
      matchedPolicyIds: [exact.id],
      confidence: "official_confirmed",
      facts,
      unknowns,
      nextActions: [{label: "내 영향표 계산하기", url: `/policy/${exact.slug}`}, ...(exact.actionUrl ? [{label: exact.actionLabel ?? "공식 원문 확인", url: exact.actionUrl}] : [])],
      citations: exact.sources.map((source) => ({title: source.title, publisher: source.publisher, url: source.url, checkedAt: source.checkedAt})),
      resolvedAt: now.toISOString()
    });
  }

  const profileContext = profile && profile.employment !== "unknown" ? `저장된 ${profile.employment === "employee" ? "근로자" : profile.employment === "self_employed" ? "자영업자" : "생활"} 조건을 공식 화면에서 함께 대조하세요.` : "지역·직업·가구 조건을 공식 화면에서 함께 대조하세요.";
  return answerSheetSchema.parse({
    query,
    outcome: "official_handoff",
    title: guide.title,
    summary: "아직 바로 답할 수 있는 정책 정보가 없어요. 담당 공식 기관에서 확인할 항목과 경로를 안내합니다.",
    topicIds: [topicId],
    matchedPolicyIds: guide.matchedPolicyIds.filter((id) => policyById.has(id)),
    confidence: "needs_context",
    facts: [...guide.checks, profileContext],
    unknowns: ["현재 적용되는 세부 제도명", "개인 조건과 기준일에 따른 최종 자격·금액"],
    nextActions: [{label: `${guide.publisher}에서 “${query.slice(0, 36)}${query.length > 36 ? "…" : ""}” 확인`, url: guide.actionUrl}],
    citations: [{title: guide.title, publisher: guide.publisher, url: guide.actionUrl, checkedAt: guide.reviewedAt}],
    resolvedAt: now.toISOString()
  });
}

export function questionCoverage(query: string) {
  const topics = topicScores(query);
  return {topicId: topics[0]?.score > 0 ? topics[0].topic.id : null, score: topics[0]?.score ?? 0, exactPolicyId: policyMatches(query)[0]?.policy.id ?? null};
}
